import { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import ComponentNotifier from '../../../../services/wss/ComponentNotifier';
import { EngineNames } from '../../../../services/wss/events/EngineNames';
import templateEngine from '../TemplateEngine';
import { buildHtml } from './IframeRendererHelper';
import userSettingsEngine from '../../../../controllers/UserSettings/UserSettingsEngine';
import tabEngine from '../../TabEngine/TabEngine';
import MarkdownRenderer from './MarkdownRenderer';
import userTopicService from '../../../../services/UserTopicService';
import AppContext from '../../../../controllers/AppContext';
import responseHelper from '../../../../services/ResponseHelper';
import logService from '../../../../services/wss/logService3';
import searchQueryEngine from '../../YourSearchTab/Core/SearchQueryEngine';

const _1MB = 1024 * 1024;
const _name = 'IframeRenderer';

export default function IframeRenderer({ entry, viewMode }) {
    const iframeRef = useRef(null);
    const skipRebuild = useRef(false);

    const rebuildIframe = () => {
        if (!entry?.tid) return;
        const template = templateEngine.getTemplate(entry.tid);
        if (!template) return;
        const dateFormat = userSettingsEngine.resolve('contentPanel.chipDateTimeFormat');
        const settings = userSettingsEngine.resolveAll ? userSettingsEngine.resolveAll() : {};
        const getByFormat = (format) => templateEngine.getTemplateByFormat(format);
        const html = buildHtml(template.body, entry.body, templateEngine.getTemplate, entry, template, dateFormat, settings, getByFormat);
        if (!html) return;
        if (iframeRef.current) iframeRef.current.srcdoc = html;
    };

    const persist = async (updatedBody) => {
        const jwt = AppContext.getToken();
        if (!entry?.rk || !jwt) return;
        const serialized = JSON.stringify(updatedBody);
        if (new Blob([serialized]).size > _1MB) return;
        skipRebuild.current = true;
        const resp = await userTopicService.updateJournalBody(entry.rk, serialized, jwt);
        const code = responseHelper.check(resp, 'persist', _name);
        if (code < 0) {
            skipRebuild.current = false;
            logService.logError(`${_name}.persist: response code: ${code}`);
        }
    };

    const parseBody = () => {
        if (!entry?.body) return null;
        try {
            return JSON.parse(entry.body);
        } catch {
            return null;
        }
    };

    useEffect(() => {
        if (skipRebuild.current) {
            skipRebuild.current = false;
            return;
        }
        rebuildIframe();
    }, [entry?.rk, entry?.tid, entry?.body, viewMode]);

    useEffect(() => {
        const handleTemplatesChanged = (e) => {
            if (e.target !== 'templatesChanged') return;
            rebuildIframe();
        };
        const unsub = ComponentNotifier.subscribe(EngineNames.TemplateEngine, handleTemplatesChanged);
        return () => unsub();
    }, [entry?.tid]);

    useEffect(() => {
        const handleMessage = (e) => {
            // alleen verwerken als het van onze eigen iframe komt
            if (e.source !== iframeRef.current?.contentWindow) return;

            if (e.data?.type === 'jerny:search') {
                const index = tabEngine.getIndexById('search');
                if (index !== -1) tabEngine.setTab(index);
                searchQueryEngine.setQuery(e.data.query);
                return;
            }
            if (e.data?.type === 'jerny:openurl') {
                const url = e.data?.url;
                if (url) window.open(url, '_blank', 'noopener,noreferrer');
                return;
            }
            if (e.data?.type === 'jerny:updatelayout') {
                const body = parseBody();
                if (!body) return;
                body.layout = e.data.layout;
                persist(body);
                return;
            }
            if (e.data?.type === 'jerny:updatebody') {
                const body = parseBody();
                if (!body) return;
                Object.assign(body, e.data.fields ?? {});
                persist(body);
                return;
            }
            if (e.data?.type === 'jerny:contextmenu') {
                const rect = iframeRef.current?.getBoundingClientRect() ?? { left: 0, top: 0 };
                window.dispatchEvent(
                    new CustomEvent('jerny:contextmenu', {
                        detail: { x: rect.left + e.data.x, y: rect.top + e.data.y },
                    }),
                );
            }
        };
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [entry?.rk, entry?.body]);

    if (viewMode === 'json') return <MarkdownRenderer entry={entry} viewMode="json" />;
    if (viewMode === 'plain')
        return (
            <Typography variant="body2" sx={{ color: 'var(--j-text)', whiteSpace: 'pre-wrap', fontSize: 13, p: 1 }}>
                {entry.body}
            </Typography>
        );
    if (viewMode === 'md') return <MarkdownRenderer entry={entry} viewMode="md" />;
    if (!entry?.tid)
        return (
            <Typography variant="caption" sx={{ color: 'var(--j-muted)', p: 1, display: 'block' }}>
                No template linked to this entry.
            </Typography>
        );
    if (!templateEngine.getTemplate(entry.tid))
        return (
            <Typography variant="caption" sx={{ color: 'var(--j-muted)', p: 1, display: 'block' }}>
                Template not found or not approved yet.
            </Typography>
        );

    return (
        <Box id="iframe-renderer-root" sx={{ width: '100%', height: '100%' }}>
            <iframe
                ref={iframeRef}
                sandbox="allow-scripts"
                style={{ width: '100%', height: '100%', border: 'none', background: '#0b1e2e' }}
                title="card-renderer"
            />
        </Box>
    );
}
